import React from 'react';
import './Avatar.css';

const Avatar = ({ user, size = 'medium', className = '', groupName = null }) => {
  // Handle group avatar - when groupName is provided, show group initials
  if (groupName) {
    const sizeClass = size === 'small' ? 'small' : size === 'large' ? 'large' : size === 'xlarge' ? 'xlarge' : '';

    const generateGroupColor = (name) => {
      if (!name) return { bg: '#667eea', text: '#ffffff' };

      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }

      const colors = [
        { bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', text: '#ffffff' },
        { bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', text: '#ffffff' },
        { bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', text: '#ffffff' },
        { bg: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', text: '#ffffff' },
        { bg: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', text: '#ffffff' },
        { bg: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', text: '#ffffff' },
      ];

      const index = Math.abs(hash) % colors.length;
      return colors[index];
    };

    const getGroupInitials = (name) => {
      if (!name) return '?';
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    };

    const colors = generateGroupColor(groupName);
    const initials = getGroupInitials(groupName);

    return (
      <div className={`avatar ${sizeClass} ${className}`}>
        <div
          className="avatar-placeholder"
          style={{
            background: colors.bg,
            color: colors.text,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span>{initials}</span>
        </div>
      </div>
    );
  }

  if (!user) return null;

  const sizeClass = size === 'small' ? 'small' : size === 'large' ? 'large' : size === 'xlarge' ? 'xlarge' : '';

  // Generate a color based on the user's name
  const generateColor = (name) => {
    if (!name) return { bg: '#e0e0e0', text: '#000000' };

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Generate pastel colors for better readability
    const colors = [
      { bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', text: '#ffffff' },
      { bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', text: '#ffffff' },
      { bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', text: '#ffffff' },
      { bg: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', text: '#ffffff' },
      { bg: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', text: '#ffffff' },
      { bg: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', text: '#ffffff' },
      { bg: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', text: '#000000' },
      { bg: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', text: '#000000' },
      { bg: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)', text: '#000000' },
      { bg: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)', text: '#000000' },
    ];

    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const colors = generateColor(user.display_name || user.username);
  const initials = getInitials(user.display_name || user.username);

  // Handle SQL nullable types from backend
  const getAvatarUrl = () => {
    if (!user.avatar_url) return null;
    if (typeof user.avatar_url === 'string') return user.avatar_url;
    if (user.avatar_url.String && user.avatar_url.Valid) return user.avatar_url.String;
    return null;
  };

  const avatarUrl = getAvatarUrl();

  return (
    <div className={`avatar ${sizeClass} ${className}`}>
      {avatarUrl ? (
        <img src={avatarUrl} alt={user.display_name || user.username} />
      ) : (
        <div
          className="avatar-placeholder"
          style={{
            background: colors.bg,
            color: colors.text,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span>{initials}</span>
        </div>
      )}
    </div>
  );
};

export default Avatar;
